export interface StrategyPillar {
  title: string
  body: string
}

export interface StrategyValue {
  number: string
  title: string
  paragraphs: string[]
  contrast?: { from: string; to: string }
}

export const strategy = {
  label: 'Strategic brief',
  eyebrow: 'Internal · strategic brief',
  title: 'Strategic brief — NOK Eye Care Pulse',
  intro: [
    'The congress environment is difficult for pharma-led engagement. Ophthalmologists are experienced, time-poor and often highly alert to being steered toward a commercial message. If the topic, wording or visual treatment feels too product-adjacent, the natural reaction is to raise their guard before the conversation has even started.',
    'The NOK Eye Care Pulse therefore takes a deliberately neutral approach in three ways:',
  ],
  pillars: [
    {
      title: 'Topic neutrality',
      body: 'The experience starts with broad, system-level questions about eye care rather than DME, a specific treatment area or a branded solution.',
    },
    {
      title: 'Question neutrality',
      body: 'The questions are framed as genuine exploration rather than leading prompts. Delegates are asked where they see the biggest opportunity, what could improve care, and what they would change themselves.',
    },
    {
      title: 'Design neutrality',
      body: 'The visual treatment should feel editorial, scientific and congress-relevant rather than promotional. AbbVie is present as the host, but not the hero.',
    },
  ] as StrategyPillar[],
  purpose: 'The purpose is to build ethos before message.',
  signalsLead: 'AbbVie signals:',
  signals: [
    'We are listening.',
    'We are interested in your view.',
    'We are not here to tell you what to think.',
  ],
  signalsNote: 'That lowers resistance and creates a more credible interaction.',
  valuesLead:
    'At the same time, the questions are not random. They are designed to generate strategically useful input around three core areas.',
  values: [
    {
      number: '1',
      title: 'Better segmentation',
      paragraphs: [
        'The answers help identify which issues matter most to different ophthalmologists. Some may prioritise diagnosis. Others treatment. Others follow-up. Some may be highly open to technology. Others may care more about workforce, time or treatment burden.',
        'That creates a richer picture than traditional demographic segmentation.',
      ],
      contrast: {
        from: 'Country · role · institution · speciality',
        to: 'What this person cares about clinically and operationally',
      },
    },
    {
      number: '2',
      title: 'Better quality of outreach',
      paragraphs: [
        'The insight can help shape future conversations so they begin from what matters to the HCP rather than from what AbbVie wants to say.',
        'If a segment consistently prioritises treatment burden, the outreach can begin there. If another group prioritises diagnosis and imaging, scientific content can move toward patient characterisation, OCT and phenotyping. If follow-up dominates, the conversation can focus on monitoring, workflow and capacity.',
      ],
      contrast: {
        from: 'More targeted communication',
        to: 'More relevant communication',
      },
    },
    {
      number: '3',
      title: 'Better quality of engagement',
      paragraphs: [
        'The data can also improve the actual subjects AbbVie chooses to bring to HCPs. Instead of repeatedly reaching out with product-led material, AbbVie can use congress insight to identify topics that are genuinely interesting to the ophthalmology community.',
      ],
      contrast: {
        from: '“We have a campaign we want to show you.”',
        to: '“We heard this was a major concern at NOK, so we explored it further.”',
      },
    },
  ] as StrategyValue[],
  deeper: {
    kicker: 'The deeper strategic value',
    title: 'AbbVie earns permission to participate in the conversation',
    paragraphs: [
      'The poll is not only a data-gathering tool. It is a relationship-positioning tool.',
      'By collecting views, showing peer comparison immediately, and sharing the collective picture back, AbbVie shifts from being perceived purely as a company that pushes messages to a company that can also listen, connect perspectives, curate insight and give something useful back.',
      'That is particularly important with a specialist audience that may not naturally see pharma as an educational authority.',
    ],
    contrast: {
      from: '“AbbVie has the answer.”',
      to: '“AbbVie helps make the conversation visible.”',
    },
  },
  oneLine: 'Neutrality is not the absence of strategy — it is the strategy.',
  oneLineSupport:
    'The neutral topic, neutral questioning and restrained design help reduce resistance and build trust, while the underlying data improves segmentation, relevance, outreach quality and future content strategy.',
  close: 'Close',
}

export const qrPanel = {
  eyebrow: 'How delegates arrive',
  src: 'https://cdn.magicpatterns.com/uploads/ijzKvpc5e2gyv7XLR9tYJB/ChatGPT_Image_Sep_16,_2026,_10_09_51_AM.png',
  alt: 'Eye-shaped QR code used to open the NOK Eye Care Pulse',
  caption: 'One code on the stand, in the programme and on rep-shared links.',
  note: 'Entry point is logged with each response, so traffic can be split by source, day and time.',
}
